import { t } from '../i18n/i18n'
import { handleMessage } from './stateHelper'
import { IDataContext } from '../interfaces/IDataContext'
import { EmailRequestDTO, RecipientDTO, WelcomeEmailStatusDTO } from '../api/schemas/schema'
import { UserAccount } from '../api/schemas/schema'
import { api } from './apiHelper'
import { emptyWelcomeEmailStatus, IWelcomeEmailStatus } from './welcomeEmailHelper'

/**
 * Välkomstmailet skickas manuellt från kundregistret. Kunder läggs ofta upp i förväg för att
 * demas, och då ska mailet inte gå iväg förrän kundens riktiga adresser är på plats.
 * Utskicket kvitteras i back-office-databasen — det är kvittot som avgör om uppdateringsmailet
 * får börja skickas.
 */
export const sendWelcomeEmail = async (
   dataContext: IDataContext,
   organisationId: string,
   userAccounts: UserAccount[],
   onSent?: () => void
) => {
   const { accessToken } = dataContext.state

   if (!userAccounts || userAccounts.length === 0) {
      handleMessage(dataContext.setRootState, 'error', t('NoRecipientsForWelcomeEmail'), '')
      return
   }

   const recipientRequest: RecipientDTO = {
      organisationId: organisationId,
      recipient: userAccounts.map((userAccount: UserAccount) => ({
         email: userAccount.email,
         name: `${userAccount.firstName} ${userAccount.lastName}`,
      })),
   }
   try {
      await api(accessToken).sendWelcomeEmail(recipientRequest)

      handleMessage(
         dataContext.setRootState,
         'success',
         t('WelcomeEmailSent'),
         `${t('MailWasSentTo')} ${recipientRequest.recipient.map((x) => x.email).join(', ')}.`
      )
      onSent?.()
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotSendWelcomeMail'), '')
   }
}

export const sendSubscriptionUpdateNotification = async (dataContext: IDataContext, request?: EmailRequestDTO) => {
   const { accessToken } = dataContext.state
   if (!request) {
      return
   }
   try {
      await api(accessToken).sendSubscriptionUpdateNotificationToUser(request)
      handleMessage(
         dataContext.setRootState,
         'success',
         t('UpdateSubscriptionEmailSent'),
         `${t('MailWasSentTo')} ${request.recipients.map((x) => x.email).join(', ')}.`
      )
   } catch (error) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotSendSubscriptionUpdateMail'), '')
   }
}

/**
 * Hämtar vilka kunder som fått välkomstmailet. Misslyckas anropet behandlas alla kunder som
 * "ej skickat", vilket håller inne uppdateringsmailet i stället för att riskera fel mail.
 */
export const getWelcomeEmailStatus = async (
   setWelcomeEmailStatus: React.Dispatch<React.SetStateAction<IWelcomeEmailStatus>>,
   dataContext: IDataContext,
   signal?: AbortSignal
) => {
   const { accessToken } = dataContext.state
   try {
      const response = await api(accessToken).getWelcomeEmailStatus({ signal })
      const data: WelcomeEmailStatusDTO = response.data ?? {}
      setWelcomeEmailStatus({
         trackingStartedAt: data.trackingStartedAt ?? undefined,
         byOrganisationId: new Map((data.statuses ?? []).map((status) => [status.organisationId, status])),
      })
   } catch (error: any) {
      if (error?.name !== 'AbortError') {
         setWelcomeEmailStatus(emptyWelcomeEmailStatus)
         handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchWelcomeEmailStatus'), '')
      }
   }
}
